import { FunctionComponent, useState } from 'react'
import Title from '@/components/_custom/Title/title'
import { AlertRange, Daycare } from '@/types/Daycare'
import styles from './alert.module.scss'
import { useMutation } from '@apollo/client'
import { SET_ALERT } from '@/modules/API/daycare/daycareHandler'
import { SelectOptionsDeprecated } from '@/types/Select'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import { PowerSettingsNew, ResetTv } from '@mui/icons-material'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import TextArea from '@/components/_custom/TextArea/textArea'
import { useAuth } from '@/modules/context/AuthContext'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface AlertProps {
    daycare: Daycare
    canUpdate: boolean
}

const RANGE_LABEL_KEYS: Record<AlertRange, string> = {
    [AlertRange.alert24]: 'alertRange24h',
    [AlertRange.alert48]: 'alertRange48h',
    [AlertRange.alert72]: 'alertRange72h',
    [AlertRange.alert7d]: 'alertRange7d',
    [AlertRange.alert14d]: 'alertRange14d'
}

const Alert: FunctionComponent<AlertProps> = ({ daycare, canUpdate }: AlertProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newComment, setNewComment] = useState<string>(daycare.alertComment)
    const [newRange, setNewRange] = useState<string>(daycare.alertRange)

    const [comment, setComment] = useState(daycare.alertComment)
    const [range, setRange] = useState(daycare.alertRange)
    const [active, setActive] = useState<boolean>(daycare.activeAlert.valueOf())

    const RANGES: AlertRange[] = Object.values(AlertRange)

    const [setAlert] = useMutation(SET_ALERT, {
        variables: {
            daycareId: daycare.id,
            active: active,
            range: range,
            comment: comment
        },
        onCompleted: () => {
            setNewComment(comment)
            setNewRange(range)

            fetchCurrentDaycare()

            showNotificationSuccess(t('alertUpdated'))
        },
        onError: () => {
            showNotificationError(t('alertUpdateError'))
        }
    })

    function handleActive(): void {
        setActive(!active)
        setAlert({
            variables: {
                daycareId: daycare.id,
                active: !active,
                range: range,
                comment: comment
            }
        })
    }

    function handleReset(): void {
        setComment(t('genericAlertComment'))
        setAlert({
            variables: {
                daycareId: daycare.id,
                active: active,
                range: range,
                comment: t('genericAlertComment')
            }
        })
    }

    const rangeOptions: SelectOptionsDeprecated = [
        {
            options: RANGES.map((range: AlertRange) => {
                return {
                    value: range.toString(),
                    name: t(RANGE_LABEL_KEYS[range])
                }
            })
        }
    ]

    function setSelectRange(value: string): void {
        setRange(value as AlertRange)
    }

    const disabled: boolean = !active || (newComment === comment && newRange === range)

    return (
        <div className={styles.AlertPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('myAnnulationAlert')} className={styles.Title} />
                    {active && canUpdate && (
                        <ButtonTextIcon
                            type={'custom'}
                            className={styles.Button}
                            onClick={handleActive}
                            customText={t('deactive')}
                            customIcon={<PowerSettingsNew />}
                        />
                    )}
                </div>
                <div className={styles.Container}>
                    {!active && (
                        <div className={styles.InputContainerDesactive}>
                            {canUpdate && (
                                <ButtonTextIcon
                                    type={'custom'}
                                    className={styles.Button}
                                    onClick={handleActive}
                                    customText={t('active')}
                                    customIcon={<PowerSettingsNew />}
                                />
                            )}
                        </div>
                    )}
                    <div className={`${styles.InputContainer}`}>
                        <div className={styles.Switcher}>
                            <div className={styles.SwitcherText}>{t('alertActiveMessage')}</div>
                            <SelectUnique
                                selectOptions={rangeOptions}
                                selectValue={range}
                                setSelectValue={setSelectRange}
                                readOnly={!active || !canUpdate}
                                className={styles.SwitcherInput}
                            />
                        </div>
                        <TextArea
                            id={'alertComment'}
                            name={'alertComment'}
                            className={styles.TextArea}
                            value={comment}
                            disabled={!active || !canUpdate}
                            onChange={setComment}
                            maxCount={200}
                            minRows={2}
                            maxRows={3}
                        />
                        {canUpdate && (
                            <div className={styles.ButtonContainer}>
                                <ButtonTextIcon
                                    type={'custom'}
                                    onClick={handleReset}
                                    customText={t('reset')}
                                    customIcon={<ResetTv />}
                                    className={styles.Button}
                                    disabled={!active}
                                />
                                <ButtonTextIcon
                                    type={'update'}
                                    onClick={setAlert}
                                    className={styles.Button}
                                    disabled={disabled}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Alert
